import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api.js';
import { useLang } from '../i18n.js';

/**
 * Sign in.
 *
 * Split panel from the design: the brand statement on the left over the
 * MotionReach gradient, the form on the right. Google first, magic link
 * underneath — and unlike v2, the magic link is a convenience rather than the
 * only way the second Workspace can get in.
 */
export default function SignIn({ googleClientId, onSignedIn, notice }) {
  const { t } = useLang();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(
    new URLSearchParams(window.location.search).get('signin_error') || '',
  );
  const googleBtn = useRef(null);

  // Google Identity Services renders its own button. Without a client id there
  // is nothing to render, so we say so plainly instead of showing a dead control.
  useEffect(() => {
    if (!googleClientId || !googleBtn.current) return;
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => {
      if (!window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        ux_mode: 'popup',
        auto_select: false,
        callback: async (res) => {
          setBusy(true);
          try {
            await api.signInWithGoogle(res.credential);
            onSignedIn();
          } catch (e) {
            setError(e.message);
          } finally {
            setBusy(false);
          }
        },
      });
      window.google.accounts.id.renderButton(googleBtn.current, {
        theme: 'outline',
        size: 'large',
        width: 320,
        text: 'continue_with',
      });
    };
    document.head.appendChild(script);
    return () => script.remove();
  }, [googleClientId, onSignedIn]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.requestMagicLink(email);
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'stretch', minHeight: '100vh' }}>
      <div
        className="signin-brand"
        style={{
          flex: 1,
          background: 'linear-gradient(150deg,#0D0720 0%,#221177 55%,#5B2D9A 100%)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: 48,
          color: '#fff',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              border: '2px dashed #FF1FA8',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span
              style={{ width: 6, height: 6, borderRadius: '50%', background: '#FF1FA8' }}
            />
          </span>
          <span
            style={{ width: 16, height: 16, borderRadius: '50%', border: '4px solid #73FDFF' }}
          />
          <span
            style={{
              fontFamily: 'var(--f-display)',
              fontWeight: 600,
              fontSize: 15,
              letterSpacing: '.02em',
            }}
          >
            Brandstorm × MotionReach
          </span>
        </div>

        <div>
          <div
            className="display"
            style={{ fontSize: 44, lineHeight: 1.1, maxWidth: 440 }}
          >
            {t('tagline')}
          </div>
          <div
            style={{
              fontSize: 15,
              lineHeight: 1.6,
              opacity: 0.75,
              maxWidth: 400,
              marginTop: 14,
            }}
          >
            {t('taglineSub')}
          </div>
        </div>

        <div style={{ fontSize: 12, opacity: 0.5 }}>CRM v3 · internal</div>
      </div>

      <div
        style={{
          width: 460,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--surface)',
        }}
      >
        <div style={{ width: 320 }}>
          <div className="display" style={{ fontSize: 24, marginBottom: 6 }}>
            {t('signIn')}
          </div>
          <div
            style={{
              fontSize: 13.5,
              color: 'var(--ink-2)',
              lineHeight: 1.55,
              marginBottom: 24,
            }}
          >
            {t('signInBlurb')}
          </div>

          {googleClientId ? (
            <div ref={googleBtn} />
          ) : (
            <div
              style={{
                padding: '10px 12px',
                border: '1px dashed var(--line-strong)',
                borderRadius: 'var(--r-md)',
                fontSize: 12.5,
                color: 'var(--ink-3)',
                lineHeight: 1.5,
              }}
            >
              Google sign-in is not configured yet — no OAuth client id. Use the magic
              link below.
            </div>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              margin: '20px 0',
              color: 'var(--ink-3)',
              fontSize: 12,
            }}
          >
            <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
            {t('or')}
            <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
          </div>

          <form onSubmit={submit}>
            <label className="field-label" htmlFor="signin-email">
              {t('workEmail')}
            </label>
            <input
              id="signin-email"
              className="input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@brandstorm.co.th"
              style={{ height: 42 }}
            />
            <button
              className="btn btn-primary btn-block"
              style={{ height: 42, marginTop: 10 }}
              disabled={busy}
            >
              {t('magicLink')}
            </button>
          </form>

          {sent && (
            <div style={{ fontSize: 12.5, color: 'var(--success)', marginTop: 12 }}>
              {t('magicSent')}
            </div>
          )}
          {error && (
            <div style={{ fontSize: 12.5, color: 'var(--danger)', marginTop: 12 }}>{error}</div>
          )}
          {/* Google accepted you but the session did not survive — a browser-side
              cookie problem, not a wrong password. Saying so is the difference
              between a diagnosable report and "the button does nothing". */}
          {notice && (
            <div
              style={{
                fontSize: 12.5,
                color: 'var(--warning)',
                marginTop: 12,
                lineHeight: 1.55,
              }}
            >
              {notice}
            </div>
          )}

          <div
            style={{
              fontSize: 12,
              color: 'var(--ink-3)',
              lineHeight: 1.6,
              marginTop: 16,
            }}
          >
            {t('magicNote')}
          </div>
        </div>
      </div>
    </div>
  );
}
